import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header/Header';
import BottomNavigation from '../../components/BottomNavigation/BottomNavigation';
import { SiTether } from 'react-icons/si';
import { FaEthereum } from 'react-icons/fa';
import Toast from '../../components/Toast';
import { formatCurrency } from '../../utils/format';
import './Spread.css';
import { useUser } from '../../context/UserContext';
import { userService } from '../../services/userService';
import { inviteService } from '../../services/inviteService';
import { withdrawService } from '../../services/withdrawService';

const Spread = ({ onTabChange }) => {
  const { t } = useTranslation();
  const { user, walletAddress } = useUser();
  const [inviteCode, setInviteCode] = useState([]);
  const [inviteLink, setInviteLink] = useState('');
  const [userStats, setUserStats] = useState({
    usdtBalance: 0,
    ethBalance: 0,
    withdrawableEarnings: 0,
  });
  const [inviteRecords, setInviteRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (walletAddress) {
      loadUserData();
    }
  }, [walletAddress, user]);

  useEffect(() => {
    if (user && user.inviteCode) {
      setInviteCode(user.inviteCode.split(''));
      setInviteLink(`${window.location.origin}?ref=${user.inviteCode}`);
    }
  }, [user]);

  const loadUserData = async () => {
    if (!walletAddress) return;
    
    try {
      setLoading(true);
      
      // 加载用户资产统计
      const statsData = await userService.getUserStats(walletAddress);
      if (statsData) {
        setUserStats({
          usdtBalance: statsData.usdtBalance || 0,
          ethBalance: statsData.ethBalance || 0,
          withdrawableEarnings: statsData.withdrawableEarnings || 0,
        });
      }
      
      // 加载邀请列表
      const invites = await inviteService.getUserInvites(walletAddress);
      if (invites) {
        setInviteRecords(invites);
      }
    } catch (error) {
      console.error('加载用户数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      Toast.success('已复制到剪贴板');
    }).catch(err => {
      console.error('复制失败:', err);
      Toast.error('复制失败');
    });
  };

  const handleWithdraw = async () => {
    if (!walletAddress) {
      Toast.warning('请先连接钱包');
      return;
    }

    if (userStats.withdrawableEarnings <= 0) {
      Toast.warning('可提取收益不足');
      return;
    }

    const amountStr = prompt(`请输入提现金额（最大 ${formatCurrency(userStats.withdrawableEarnings)}）:`, formatCurrency(userStats.withdrawableEarnings));
    if (!amountStr) {
      return; // 用户取消
    }
    
    const amount = parseFloat(amountStr);
    
    if (isNaN(amount) || amount <= 0) {
      Toast.error('请输入有效的提现金额');
      return;
    }

    if (amount > userStats.withdrawableEarnings) {
      Toast.error('提现金额不能超过可提取收益');
      return;
    }

    try {
      setLoading(true);
      const result = await withdrawService.createWithdraw(walletAddress, amount, 'USDT');
      Toast.success('提现申请成功！');
      // 刷新数据
      loadUserData();
    } catch (error) {
      const errorMsg = error.message || '未知错误';
      if (errorMsg.includes('登录已过期')) {
        Toast.error(errorMsg);
      } else {
        Toast.error('提现失败: ' + errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="spread-page">
      <Header />
      <div className="spread-content">
        {/* Invite connection */}
        <section className="spread-card">
          <header className="spread-section-header">
            <div className="spread-title-bar"></div>
            <h3>{t('spread.inviteConnection')}</h3>
          </header>

          <div className="spread-field">
            <label className="spread-label">{t('spread.invitationCode')}</label>
            <div className="code-chips">
              {inviteCode.map((ch, idx) => (
                <span key={idx} className="chip">{ch}</span>
              ))}
              <button 
                type="button" 
                className="chip copy" 
                aria-label="copy"
                onClick={() => copyToClipboard(inviteCode.join(''))}
              >
                📋
              </button>
            </div>
          </div>

          <div className="spread-field">
            <label className="spread-label">{t('spread.invitationLink')}</label>
            <div className="link-row">
              <input className="link-input" value={inviteLink} readOnly />
              <button 
                type="button" 
                className="copy-btn" 
                aria-label="copy"
                onClick={() => copyToClipboard(inviteLink)}
              >
                📋
              </button>
            </div>
          </div>
        </section>

        {/* User's revenue */}
        <section className="spread-card">
          <header className="spread-section-header">
            <div className="spread-title-bar"></div>
            <h3>{t('spread.userRevenue')}</h3>
          </header>

          <div className="assets-grid">
            <div className="asset-card">
              <div className="asset-icon usdt"><SiTether /></div>
              <div className="asset-name">USDT</div>
              <div className="asset-value green">{userStats.usdtBalance}</div>
            </div>
            <div className="asset-card">
              <div className="asset-icon eth"><FaEthereum /></div>
              <div className="asset-name">ETH</div>
              <div className="asset-value gold">{userStats.ethBalance}</div>
            </div>
          </div>

          <div className="withdraw-card">
            <div className="withdraw-info">
              <span className="withdraw-label">{t('spread.withdrawableEarnings')}</span>
              <span className="withdraw-value">{userStats.withdrawableEarnings}</span>
            </div>
            <button 
              type="button" 
              className="draw-btn"
              onClick={handleWithdraw}
              disabled={loading || !walletAddress || userStats.withdrawableEarnings <= 0}
            >
              {loading ? t('spread.processing') : t('spread.draw')}
            </button>
          </div>
        </section>

        {/* Invitation List */}
        <section className="spread-panel">
          <header className="spread-section-header">
            <div className="spread-title-bar"></div>
            <h3>{t('spread.invitationList')}</h3>
          </header>

          {inviteRecords.length === 0 ? (
            <>
              <div className="spread-table-header">
                <div className="th">{t('spread.walletAddress')}</div>
                <div className="th right">{t('spread.quantity')}</div>
              </div>
              <div className="spread-empty">
                <div className="spread-empty-illustration">🧑‍💻</div>
                <p>{t('spread.noData')}</p>
              </div>
            </>
          ) : (
            <>
              <div className="spread-table-header">
                <div className="th">{t('spread.walletAddress')}</div>
                <div className="th right">{t('spread.quantity')}</div>
              </div>
              <div className="spread-table-body">
                {inviteRecords.map((record) => (
                  <div key={record.id} className="spread-table-row">
                    <div className="td">{record.inviteeAddress}</div>
                    <div className="td right">{record.rewardAmount || 0}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      </div>
      <BottomNavigation activeTab="spread" onTabChange={onTabChange} />
    </div>
  );
};

export default Spread;
